import { inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import {
  Event,
  EventBus,
  GuidedTourService,
  UnleashService,
} from '@thiememeulenhoff/ng-sdk';
import {
  FEATURE_IMPRESSION_EVENT,
  FeatureImpressionEvent,
  NAVIGATE_EVENT,
  TOURGUIDE_EVENT,
  UserActivityEvent,
  UserActivityEventType,
} from '@thiememeulenhoff/types';
import { filter } from 'rxjs';
import { UserSettingStore } from '@thiememeulenhoff/ng-store';

@Injectable({
  providedIn: 'root',
})
export class UserActivityService {
  readonly #eventBus = new EventBus<UserActivityEvent>();

  readonly #router = inject(Router);

  readonly #userSettingStore = inject(UserSettingStore);

  readonly #unleashService = inject(UnleashService);

  readonly #guidedTourService = inject(GuidedTourService);

  #previousUrl: string | null = null;

  initialize() {
    this.#router.events
      .pipe(
        takeUntilDestroyed(),
        filter((e) => e instanceof NavigationEnd),
      )
      .subscribe((item) => {
        this.emit(NAVIGATE_EVENT, {
          url: item.url,
          previous: this.#previousUrl,
        });

        dispatchEvent(new Event(NAVIGATE_EVENT));

        this.#previousUrl = item.url;
      });

    this.#listenForGlobalActivity();
    this.#listenForFeatureImpression();
    this.#listenForGuidedTourEvents();
  }

  // Listen for the 'tmwc-activity' global event
  #listenForGlobalActivity() {
    window.addEventListener('tmwc-activity', (event) => {
      const customEvent = event as CustomEvent;
      this.emit(
        customEvent.detail.type,
        customEvent.detail as UserActivityEvent,
      );
    });
  }

  // Listen for Guided Tour events
  #listenForGuidedTourEvents() {
    this.#guidedTourService.on('tour', (event) => {
      if (!event.data) return;

      this.emit(TOURGUIDE_EVENT, {
        action: event.data.action,
        step: event.data.stepId,
        tourName: event.data.tourName,
      });

      this.#userSettingStore.setUserSetting({
        key: event.data.tourName ?? '',
        context: event.data.stepId,
        value: event.data.action,
      });
    });
  }

  // When a featureFlag is evaluated, we emit an event
  #listenForFeatureImpression() {
    this.#unleashService.on('impression', (event) => {
      if (!event.data) return;

      const defaultVariantValue = event.data.enabled
        ? event.data.featureName
        : 'Control';

      this.emit(FEATURE_IMPRESSION_EVENT, {
        featureName: event.data.featureName,
        variant: event.data.variant || defaultVariantValue,
        payload: event.data.payload || {},
      } as FeatureImpressionEvent);
    });
  }

  emit(key: UserActivityEventType, data: UserActivityEvent) {
    this.#eventBus.emit(key, data);
  }

  on(
    key: UserActivityEventType,
    callback: (data: Event<UserActivityEvent>) => void,
  ) {
    return this.#eventBus.on(key, callback);
  }
}
